<template>
  <div
    ref="root"
    :class="[
      'form-upload-image',
      {
        'form-upload-image--filled': hasFile
      }
    ]"
  >
    <div v-if="hasFile" key="preview" class="form-upload-image__preview">
      <img
        class="form-upload-image__preview__image"
        :src="previewImageURL"
        alt="form upload preview image"
      />

      <div
        v-if="!locked"
        class="form-upload-image__preview__button"
        @click="onRemove"
      >
        <i class="icon-delete"></i>
      </div>
    </div>

    <div
      v-else-if="!isUploading"
      key="placeholder"
      class="form-upload-image__placeholder"
    >
      <div class="form-upload-image__placeholder__icon">
        <i class="icon-plus"></i>
      </div>
      <div v-if="placeholder" class="form-upload-image__placeholder__text">
        {{ placeholder }}

        <div v-if="note" class="form-upload-image__placeholder__text__note">
          {{ note }}
        </div>
      </div>
    </div>

    <div v-else key="loader" class="form-upload-image__loader">
      <b-spinner label="Loading" />
    </div>

    <input
      :id="fileInputId"
      ref="image"
      class="form-upload-image__input"
      type="file"
      accept="image/gif, image/jpeg, image/png"
      :name="fileInputId"
      :value="input"
      @change="onFileSelect"
    />
  </div>
</template>

<script>
import Image from '@/models/Image'

export default {
  name: 'FormUploadImage',
  components: {},
  props: {
    value: Object,
    placeholder: String,
    note: String,
    locked: Boolean,
    noPreview: Boolean,
    fileInputId: {
      type: String,
      default: 'file'
    }
  },
  data() {
    return {
      isUploading: false,
      fileUpload: this.value,
      input: undefined // used to clear the dom input node value.
    }
  },
  computed: {
    hasFile() {
      return !!this.fileUpload
    },
    previewImageURL() {
      if (!this.hasFile) {
        return
      }

      const temp = new Image(this.fileUpload)
      return temp.thumbnailSizeUrl
    }
  },
  watch: {
    value(newValue) {
      this.fileUpload = newValue
    },
    fileUpload(newValue, oldValue) {
      this.$emit('input', newValue)

      if (newValue) {
        this.$emit('added', newValue)
      } else {
        this.$emit('removed', oldValue)
      }
    }
  },
  methods: {
    async onFileSelect() {
      this.isUploading = true

      try {
        const { fileUpload } = await this.$store.dispatch(
          'upload/uploadImage',
          {
            file: this.$refs.image.files[0]
          }
        )

        if (this.noPreview) {
          this.$emit('input', fileUpload)
        } else {
          this.fileUpload = fileUpload
        }
      } catch (err) {
        alert(err.message)

        this.onRemove()
      }

      this.isUploading = false
    },
    onRemove() {
      this.fileUpload = undefined
      this.input = undefined
    }
  }
}
</script>

<style lang="scss">
.form-upload-image {
  background-color: $gray-100;
  border: 1px dashed $border-color;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 250ms ease;
  position: relative;

  $root: &;

  &:hover {
    background: darken($gray-100, 2%);
  }

  &__preview {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    &__image {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }

    &__button {
      position: absolute;
      right: 0;
      top: 0;
      width: 2.8rem;
      height: 2.5rem;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $gray-400;
      transition: color 150ms ease;
      font-size: 1.2rem;
      z-index: 11;

      &:hover {
        color: $gray-700;
      }
    }
  }

  &__placeholder {
    text-align: center;

    &__icon {
      font-size: 1.2rem;
      color: $gray-500;

      i {
        display: block;
      }
    }

    &__text {
      color: $gray-700;
      font-size: 1rem;
      margin-top: 0.6rem;

      &__note {
        font-size: 0.8rem;
        color: $gray-500;
      }
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__input {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    opacity: 0;
    padding: 0;
    width: 100%;
    z-index: 10;
    cursor: pointer;
  }

  /**
     * states
     */
  &--filled {
    background: transparentize($blue-900, 0.9);
    border-color: transparent;
  }

  &:after {
    content: '';
    padding-top: 100%;
  }
}
</style>
